/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useState, useMemo } from "react"
import sortBy from "utils/sorter"
import { api } from "utils/api"
import { arrayOf, shape, string, func, array, number, bool } from "prop-types"
import SectionHeader from "./SectionHeader"
import Question from "./Question"
import { camelToSnakeKeys } from "utils/helpers"

const QuestionForm = ({
  group,
  signup,
  question: questionFromProps,
  onSave = () => {},
  onCancel = () => {},
  onDelete = () => {},
  savePath,
  sectionHeaderKind = false,
  editing = false
}) => {
  const attendeeTypes = useMemo(() => {
    return sortBy("position", Object.values(signup.attendeeTypes)).map(at => {
      return { id: at.id, name: at.nameWithRestriction }
    })
  }, [signup])

  const initQuestion = {
    kind: sectionHeaderKind ? "section_header" : "text",
    question: "",
    limitQuestions: false,
    restrictedToAdmin: false,
    publiclyRequired: false,
    visibleToAttendeeTypeIds: attendeeTypes.map(at => at.id),
    multipleSelect: false,
    choices: []
  }

  const [question, setQuestion] = useState(questionFromProps || initQuestion)
  const [saveStatus, setSaveStatus] = useState(null)

  const handleSave = () => {
    setSaveStatus({
      text: "Saving...",
      icon: "spinner"
    })
    const isNotDropdown = question.kind !== "dropdownsingle" && question.kind !== "dropdownmult"
    return api
      .putOrPost(question.id, `${savePath}${question.id ? `/${question.id}` : ""}`, {
        question: {
          ...camelToSnakeKeys(question),
          kind: isNotDropdown ? question.kind : "dropdown",
          group: group
        }
      })
      .then(resp => onSave(resp.question))
  }

  const handleDelete = () => {
    alertModal(
      {
        title: "Delete question and answers?",
        text: "This can't be undone.",
        type: "warning",
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "No, don't delete it",
        customClass: "destructive-warning",
        showCancelButton: true
      },
      () => {
        setSaveStatus({
          text: "Deleting...",
          icon: "spinner"
        })

        return api.del(`${savePath}/${question.id}`).then(_resp => onDelete(question))
      }
    )
  }

  const handleQuestionChanged = e => {
    return setQuestion({ ...question, ...{ question: e.target.value } })
  }

  const handleRestrictionChanged = event => {
    let restrictedToAdmin = false
    let publiclyRequired = false

    if (event.target.value === "required") {
      publiclyRequired = true
    } else if (event.target.value === "hidden") {
      restrictedToAdmin = true
    }

    return setQuestion({
      ...question,
      ...{ restrictedToAdmin, publiclyRequired }
    })
  }

  const handleLimitChanged = () => {
    return setQuestion({
      ...question,
      ...{
        limitQuestions: !question.limitQuestions,
        visibleToAttendeeTypeIds: attendeeTypes.map(at => at.id)
      }
    })
  }

  const handleLimitAttendeeTypeChanged = e => {
    const id = Number(e.target.value)
    return setQuestion({
      ...question,
      ...{
        visibleToAttendeeTypeIds: e.target.checked
          ? [...question.visibleToAttendeeTypeIds, id]
          : question.visibleToAttendeeTypeIds.filter(at => at !== id)
      }
    })
  }

  const handleKindChange = e => {
    const kind = e.target.value
    return setQuestion({
      ...question,
      ...{
        kind,
        multipleSelect: kind !== "dropdownmult" ? false : true,
        choices: kind !== "dropdownsingle" || kind != "dropdownmult" ? [] : question.choices
      }
    })
  }

  const handleMultipleSelectChange = () => {
    return setQuestion({
      ...question,
      ...{ multipleSelect: !question.multipleSelect }
    })
  }

  const handleAddChoice = newChoice => {
    return setQuestion({
      ...question,
      ...{ choices: [...question.choices, newChoice] }
    })
  }

  const handleUpdateChoice = ({ choice, id }) => {
    const newChoices = [...question.choices]
    newChoices[id] = choice
    return setQuestion({
      ...question,
      ...{ choices: newChoices }
    })
  }

  const handleDeleteChoice = oldChoiceId => {
    question.choices.splice(oldChoiceId, 1)
    return setQuestion({
      ...question,
      ...{ choices: [...question.choices] }
    })
  }

  const handleOptionsReorder = orderedItems => {
    return new Promise(resolve => {
      setQuestion({
        ...question,
        ...{ choices: orderedItems.map(c => c.choice) }
      })
      return resolve
    })
  }

  const handleDescriptionChanged = e => {
    return setQuestion({ ...question, ...{ description: e.target.value } })
  }

  const FormComponent = sectionHeaderKind ? SectionHeader : Question

  return (
    <FormComponent
      group={group}
      editing={editing}
      question={question}
      attendeeTypes={attendeeTypes}
      onQuestionChanged={handleQuestionChanged}
      onDescriptionChanged={handleDescriptionChanged}
      onLimitChanged={handleLimitChanged}
      onKindChanged={handleKindChange}
      onOptionsReorder={handleOptionsReorder}
      onMultipleSelectChange={handleMultipleSelectChange}
      onLimitAttendeeTypeChanged={handleLimitAttendeeTypeChanged}
      onRestrictionChanged={handleRestrictionChanged}
      onAddChoice={handleAddChoice}
      onUpdateChoice={handleUpdateChoice}
      onDeleteChoice={handleDeleteChoice}
      isSimpleEvent={signup.isSimpleEvent}
      saveStatus={saveStatus}
      onCancel={onCancel}
      handleSave={handleSave}
      handleDelete={handleDelete}
    />
  )
}

QuestionForm.propTypes = {
  group: string.isRequired,
  onCancel: func,
  onSave: func,
  onDelete: func,
  navigate: func,
  editing: bool,
  question: shape({
    id: number,
    visibleToAttendeeTypeIds: array,
    question: string,
    type: string,
    description: string
  }),
  signup: shape({
    attendeeTypes: arrayOf(
      shape({
        id: number,
        nameWithRestriction: string
      })
    )
  }),
  savePath: string,
  location: shape({
    query: shape({
      kind: string
    })
  }),
  sectionHeaderKind: bool
}

export default QuestionForm
